import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import React, { useState } from "react";
import {
  formatNumber,
  toLocaleShortMonthAndYear,
} from "../common/formatHelper";

import { ReactComponent as ChartBarIcon } from "../../components/icons/chart_bar_icon.svg";
import { ReactComponent as ChartLineIcon } from "../../components/icons/chart_line_icon.svg";
import dateHelper from "../common/dateHelper";
import forecastViewType from "../../constants/forecastViewType";

const isNoneChartType = (c) =>
  !c.chartType || c.chartType.toLowerCase() === "none";

const DbForecastChart = (props) => {
  const [, , getDateInUTC] = dateHelper();
  const [legendDisplay, setLegendDisplay] = useState({});
  const xAxisSecondaryLabels = [];
  const todayDateLabel = toLocaleShortMonthAndYear(
    props.config.referenceLineDate
      ? getDateInUTC(props.config.referenceLineDate)
      : new Date(),
  );
  const showXAxisYearsLabelAfterMonths = props.config
    .showXAxisYearsLabelAfterMonths
    ? props.config.showXAxisYearsLabelAfterMonths
    : 12;

  // Series that are plotted (visible, with a real chart type).
  const plottedConfigurations = props.config.attributeConfigurations.filter(
    (c) => c.isVisible && !isNoneChartType(c),
  );

  // Stack groups. A group is defined by the plotted series sharing a StackGroup.
  // A configuration row with the same StackGroup and ChartType "None" (typically the
  // calculated total, e.g. TotalDemand = quantity,exquantity) is not plotted; it names
  // the stack in the legend and tooltip and supplies its legend colour.
  const stackGroups = {};
  plottedConfigurations.forEach((c) => {
    if (!c.stackGroup) {
      return;
    }
    if (!stackGroups[c.stackGroup]) {
      stackGroups[c.stackGroup] = { members: [], label: null, color: null };
    }
    stackGroups[c.stackGroup].members.push(c.attributeName.toLowerCase());
  });
  props.config.attributeConfigurations.forEach((c) => {
    if (!c.stackGroup || !stackGroups[c.stackGroup] || !isNoneChartType(c)) {
      return;
    }
    stackGroups[c.stackGroup].label = c.displayName;
    stackGroups[c.stackGroup].color = c.chartColor;
  });
  const stackGroupByDataKey = {};
  Object.keys(stackGroups).forEach((group) => {
    stackGroups[group].members.forEach((key) => {
      stackGroupByDataKey[key] = group;
    });
  });

  const getStackGroupLabel = (group) => stackGroups[group].label || "Total";

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Sum of the stack per group. Hidden series are absent from the payload,
      // so the total reflects the segments currently shown.
      const totals = {};
      payload.forEach((p) => {
        const group = stackGroupByDataKey[p.dataKey];
        if (!group) {
          return;
        }
        if (!totals[group]) {
          totals[group] = { count: 0, total: 0, fill: p.fill };
        }
        totals[group].count += 1;
        const num = Number(p.value);
        totals[group].total += Number.isFinite(num) ? num : 0;
      });

      return (
        <div className="chart-tooltip">
          <div className="chart-label">{label}</div>
          {payload.map((p) => {
            return (
              <div className="chart-item-container" key={p.name}>
                <div className="pill" style={{ backgroundColor: p.fill }}></div>
                <div className="chart-item">{`${p.name} : ${formatNumber(
                  p.value,
                  props.config.decimalPlaces,
                )} `}</div>
              </div>
            );
          })}
          {Object.keys(totals)
            .filter((group) => totals[group].count > 1)
            .map((group) => {
              const t = totals[group];
              return (
                <div
                  className="chart-item-container"
                  key={`stack-total-${group}`}
                >
                  <div
                    className="pill"
                    style={{
                      backgroundColor: stackGroups[group].color || t.fill,
                    }}
                  ></div>
                  <div className="chart-item">{`${getStackGroupLabel(
                    group,
                  )} : ${formatNumber(
                    t.total,
                    props.config.decimalPlaces,
                  )} `}</div>
                </div>
              );
            })}
        </div>
      );
    }

    return null;
  };

  const isStackHidden = (group) =>
    stackGroups[group].members.every((key) => legendDisplay[key]);

  const CustomLegend = ({ payload }) => {
    if (payload && payload.length) {
      // One extra, non-series legend entry per stack group, placed after its last member.
      const items = [];
      payload.forEach((p, index) => {
        items.push({ ...p, isStack: false });
        const group = stackGroupByDataKey[p.dataKey];
        if (!group || stackGroups[group].members.length < 2) {
          return;
        }
        const lastMemberKey =
          stackGroups[group].members[stackGroups[group].members.length - 1];
        if (p.dataKey === lastMemberKey) {
          items.push({
            isStack: true,
            group,
            dataKey: `stack:${group}`,
            value: getStackGroupLabel(group),
            color: stackGroups[group].color || p.color,
            type: "bar",
          });
        }
      });

      return (
        <ul className="recharts-default-legend" key={"recharts-legend-ul"}>
          {items.map((p) => {
            const hidden = p.isStack
              ? isStackHidden(p.group)
              : legendDisplay[p.dataKey];
            const color = hidden ? "#b0bac5" : p.color;
            return (
              <li
                className="recharts-legend-item"
                style={{
                  color: color,
                }}
                key={p.value}
                onClick={(e) =>
                  p.isStack
                    ? handleStackLegendOnClick(p.group)
                    : handleLegendOnClick(p)
                }
              >
                {p.type === "line" ? (
                  <ChartLineIcon></ChartLineIcon>
                ) : (
                  <ChartBarIcon></ChartBarIcon>
                )}

                <span
                  className="recharts-legend-item-text"
                  style={{ color: color }}
                >
                  {p.value}
                </span>
              </li>
            );
          })}
        </ul>
      );
    }

    return null;
  };

  const handleLegendOnClick = (o) => {
    const { dataKey } = o;
    setLegendDisplay((op) => ({ ...op, [dataKey]: !op[dataKey] }));
  };

  // Toggles every segment of the stack together.
  const handleStackLegendOnClick = (group) => {
    const hide = !isStackHidden(group);
    setLegendDisplay((op) => {
      const next = { ...op };
      stackGroups[group].members.forEach((key) => {
        next[key] = hide;
      });
      return next;
    });
  };

  const labelRender = (args) => {
    var value = parseInt(args);
    if (value >= 1000 && value < 99999) {
      var kText = (value / 1000).toFixed(2);
      return kText + "K";
    } else if (value > 99999) {
      var text = (value / 1000000).toFixed(2);
      return text + "M";
    }

    return args;
  };

  const xAxisPrimaryTickFormatter = (tick) => {
    if (props.data.length > showXAxisYearsLabelAfterMonths) {
      return tick.substring(0, 3);
    }
    return tick;
  };

  const xAxisSecondaryTickRender = (tickProps) => {
    const { x, y, payload } = tickProps;
    const { value } = payload;
    const date = new Date(value);
    const year = date.getFullYear();

    const yearExists = xAxisSecondaryLabels.find((f) => f.year === year);

    if (yearExists) {
      const forecastdateExists = xAxisSecondaryLabels.find(
        (f) => f.forecastdate === value,
      );

      if (forecastdateExists) {
        return <text x={x} y={y} textAnchor="middle">{`${year}`}</text>;
      }
    } else {
      xAxisSecondaryLabels.push({ year: year, forecastdate: value });
      return <text x={x} y={y} textAnchor="middle">{`${year}`}</text>;
    }

    return null;
  };

  return (
    <div className="col-12">
      <div className="card shadow-sm rounded-extra-lg pt-3">
        <div
          className="card-body ps-0"
          key="forecastchart"
          style={{ width: "100%", height: 320 }}
        >
          <ResponsiveContainer>
            <ComposedChart data={props.data}>
              <CartesianGrid stroke="#dfe6ee" />
              <XAxis
                xAxisId="0"
                dataKey="label"
                stroke="#b0bac5"
                interval={0}
                tickFormatter={xAxisPrimaryTickFormatter}
              />
              {props.data.length > showXAxisYearsLabelAfterMonths ? (
                <XAxis
                  xAxisId="1"
                  dataKey="forecastdate"
                  stroke="#b0bac5"
                  axisLine={false}
                  tickLine={false}
                  tick={xAxisSecondaryTickRender}
                  interval={0}
                  height={1}
                />
              ) : null}

              <YAxis tickFormatter={labelRender} stroke="#b0bac5" />
              {props.currentView === forecastViewType.HistoricView ? (
                <ReferenceLine
                  x={todayDateLabel}
                  strokeWidth={2}
                  stroke={
                    props.config.referenceLineColor
                      ? props.config.referenceLineColor
                      : "#ff6253"
                  }
                  strokeDasharray="3 3"
                />
              ) : null}
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
              {plottedConfigurations.map((c) =>
                c.chartType === "Line" ? (
                  <Line
                    type="linear"
                    dataKey={c.attributeName.toLowerCase()}
                    stroke={c.chartColor}
                    fill={c.chartColor}
                    name={c.displayName}
                    hide={legendDisplay[c.attributeName.toLowerCase()]}
                    key={c.attributeName.toLowerCase()}
                  />
                ) : (
                  <Bar
                    dataKey={c.attributeName.toLowerCase()}
                    barSize={25}
                    fill={c.chartColor}
                    name={c.displayName}
                    // Bars sharing a stackId are drawn on top of each other,
                    // bottom-to-top in configuration order.
                    stackId={c.stackGroup ? c.stackGroup : undefined}
                    hide={legendDisplay[c.attributeName.toLowerCase()]}
                    key={c.attributeName.toLowerCase()}
                  />
                ),
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default DbForecastChart;
